import React, { useCallback, useState, useEffect } from 'react';
import { Film, Clock, Repeat, Gauge } from 'lucide-react';
import { useAnimationStore } from '../../store/animationStore';
import { MIN_DURATION, MAX_DURATION } from '../../../core/types/animation.types';
import { cn } from '../../utils/cn';

interface AnimationPropertiesProps {
  className?: string;
}

export const AnimationProperties: React.FC<AnimationPropertiesProps> = ({ className }) => {
  const {
    selectedAnimationId,
    animations,
    updateAnimation,
  } = useAnimationStore();

  const animation = selectedAnimationId ? animations[selectedAnimationId] : null;

  // Local state for input fields to allow typing
  const [localName, setLocalName] = useState('');
  const [localDuration, setLocalDuration] = useState('');
  const [localSpeed, setLocalSpeed] = useState('');
  const [localLoopCount, setLocalLoopCount] = useState('');

  // Sync local state with animation changes
  useEffect(() => {
    if (animation) {
      setLocalName(animation.name);
      setLocalDuration(animation.duration.toString());
      setLocalSpeed(animation.speed.toString());
      setLocalLoopCount(animation.loopCount.toString());
    }
  }, [animation]);

  // Handle name change
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalName(e.target.value);
  }, []);

  const handleNameBlur = useCallback(() => {
    if (animation && localName.trim() && localName !== animation.name) {
      updateAnimation(animation.id, { name: localName.trim() });
    }
  }, [animation, localName, updateAnimation]);

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  }, []);

  // Handle duration change
  const handleDurationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalDuration(e.target.value);
  }, []);

  const handleDurationBlur = useCallback(() => {
    if (!animation) return;

    const value = parseFloat(localDuration);
    if (!isNaN(value)) {
      const clampedValue = Math.max(MIN_DURATION, Math.min(MAX_DURATION, value));
      updateAnimation(animation.id, { duration: clampedValue });
      setLocalDuration(clampedValue.toString());
    } else {
      setLocalDuration(animation.duration.toString());
    }
  }, [animation, localDuration, updateAnimation]);

  // Handle speed change
  const handleSpeedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSpeed(e.target.value);
  }, []);

  const handleSpeedBlur = useCallback(() => {
    if (!animation) return;

    const value = parseFloat(localSpeed);
    if (!isNaN(value)) {
      const clampedValue = Math.max(0.1, Math.min(10, value));
      updateAnimation(animation.id, { speed: clampedValue });
      setLocalSpeed(clampedValue.toString());
    } else {
      setLocalSpeed(animation.speed.toString());
    }
  }, [animation, localSpeed, updateAnimation]);

  // Handle loop toggle
  const handleLoopChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (animation) {
      updateAnimation(animation.id, { loop: e.target.checked });
    }
  }, [animation, updateAnimation]);

  // Handle loop count change
  const handleLoopCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalLoopCount(e.target.value);
  }, []);

  const handleLoopCountBlur = useCallback(() => {
    if (!animation) return;

    const value = parseInt(localLoopCount, 10);
    if (!isNaN(value)) {
      const clampedValue = Math.max(0, Math.min(999, value));
      updateAnimation(animation.id, { loopCount: clampedValue });
      setLocalLoopCount(clampedValue.toString());
    } else {
      setLocalLoopCount(animation.loopCount.toString());
    }
  }, [animation, localLoopCount, updateAnimation]);

  // Common input blur handler for keyboard
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      // Reset to original value
      if (animation) {
        setLocalName(animation.name);
        setLocalDuration(animation.duration.toString());
        setLocalSpeed(animation.speed.toString());
        setLocalLoopCount(animation.loopCount.toString());
      }
    }
  }, [animation]);

  if (!animation) {
    return (
      <div className={cn('flex flex-col items-center justify-center h-full py-8 text-zinc-500', className)}>
        <Film size={32} className="mb-2 opacity-50" />
        <span className="text-sm">No animation selected</span>
        <span className="text-xs mt-1">Select an animation to edit its properties</span>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 p-3', className)}>
      {/* Section header */}
      <div className="flex items-center gap-2 text-sm font-medium text-zinc-300">
        <Film size={14} />
        Animation Properties
      </div>

      {/* Name */}
      <div className="space-y-1">
        <label className="text-xs text-zinc-500">Name</label>
        <input
          type="text"
          value={localName}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          onKeyDown={handleNameKeyDown}
          className={cn(
            'w-full bg-zinc-800 text-zinc-200 text-sm px-2 py-1.5 rounded',
            'border border-zinc-700 focus:border-blue-500 focus:outline-none',
            'transition-colors'
          )}
          placeholder="Animation name"
        />
      </div>

      {/* Duration */}
      <div className="space-y-1">
        <label className="flex items-center gap-1.5 text-xs text-zinc-500">
          <Clock size={12} />
          Duration (seconds)
        </label>
        <input
          type="number"
          value={localDuration}
          onChange={handleDurationChange}
          onBlur={handleDurationBlur}
          onKeyDown={handleKeyDown}
          min={MIN_DURATION}
          max={MAX_DURATION}
          step={0.1}
          className={cn(
            'w-full bg-zinc-800 text-zinc-200 text-sm px-2 py-1.5 rounded',
            'border border-zinc-700 focus:border-blue-500 focus:outline-none',
            'transition-colors'
          )}
        />
      </div>

      {/* Speed */}
      <div className="space-y-1">
        <label className="flex items-center gap-1.5 text-xs text-zinc-500">
          <Gauge size={12} />
          Playback Speed
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={localSpeed}
            onChange={handleSpeedChange}
            onBlur={handleSpeedBlur}
            onKeyDown={handleKeyDown}
            min={0.1}
            max={10}
            step={0.1}
            className={cn(
              'flex-1 bg-zinc-800 text-zinc-200 text-sm px-2 py-1.5 rounded',
              'border border-zinc-700 focus:border-blue-500 focus:outline-none',
              'transition-colors'
            )}
          />
          <span className="text-xs text-zinc-500">x</span>
        </div>
        {/* Speed presets */}
        <div className="flex gap-1 mt-1">
          {[0.25, 0.5, 1, 2, 4].map((preset) => (
            <button
              key={preset}
              className={cn(
                'px-2 py-0.5 text-xs rounded transition-colors',
                animation.speed === preset
                  ? 'bg-blue-600 text-white'
                  : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-zinc-200'
              )}
              onClick={() => updateAnimation(animation.id, { speed: preset })}
            >
              {preset}x
            </button>
          ))}
        </div>
      </div>

      {/* Loop settings */}
      <div className="space-y-2">
        <label className="flex items-center gap-1.5 text-xs text-zinc-500">
          <Repeat size={12} />
          Loop Settings
        </label>

        {/* Loop checkbox */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={animation.loop}
            onChange={handleLoopChange}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
          />
          <span className="text-sm text-zinc-300">Enable loop</span>
        </label>

        {/* Loop count (only shown when loop is enabled) */}
        {animation.loop && (
          <div className="flex items-center gap-2 pl-6">
            <span className="text-xs text-zinc-500">Count:</span>
            <input
              type="number"
              value={localLoopCount}
              onChange={handleLoopCountChange}
              onBlur={handleLoopCountBlur}
              onKeyDown={handleKeyDown}
              min={0}
              max={999}
              className={cn(
                'w-20 bg-zinc-800 text-zinc-200 text-sm px-2 py-1 rounded',
                'border border-zinc-700 focus:border-blue-500 focus:outline-none',
                'transition-colors'
              )}
            />
            <span className="text-xs text-zinc-500">(0 = infinite)</span>
          </div>
        )}
      </div>

      {/* Info section */}
      <div className="border-t border-zinc-700 pt-3 mt-2">
        <div className="text-xs text-zinc-500 space-y-1">
          <div className="flex justify-between">
            <span>Tracks:</span>
            <span className="text-zinc-400">{animation.tracks.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Keyframes:</span>
            <span className="text-zinc-400">
              {animation.tracks.reduce((sum, track) => sum + track.keyframes.length, 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Created:</span>
            <span className="text-zinc-400">
              {new Date(animation.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Modified:</span>
            <span className="text-zinc-400">
              {new Date(animation.modifiedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
